import type { LucideIcon } from "lucide-react";
import {
  Database,
  FileClock,
  Fingerprint,
  Home,
  IdCard,
  KeyRound,
  ListChecks,
  Palette,
  ScrollText,
  Shield,
  Users,
  UsersRound,
} from "lucide-react";
import type { Permission } from "@platform/core";
import type { MessageKey } from "./i18n";

export type NavPath =
  | "/"
  | "/users"
  | "/sign-in-method"
  | "/roles"
  | "/groups"
  | "/custom-fields"
  | "/oauth-clients"
  | "/audit-logs"
  | "/settings/branding"
  | "/settings/data"
  | "/settings/user-types"
  | "/settings/log-retention";

export interface NavItem {
  id: string;
  to: NavPath;
  label: MessageKey;
  icon: LucideIcon;
  permission: Permission;
}

export interface NavSection {
  id: string;
  label: MessageKey;
  /** Docs page (relative to docsBaseUrl) for the help icon. */
  docs: string;
  items: NavItem[];
}

export const OVERVIEW: NavItem = {
  id: "overview",
  to: "/",
  label: "nav.overview",
  icon: Home,
  permission: "users:read",
};

export const SECTIONS: NavSection[] = [
  {
    id: "authentication",
    label: "nav.authentication",
    docs: "architecture.md",
    items: [
      { id: "users", to: "/users", label: "nav.users", icon: Users, permission: "users:read" },
      {
        id: "sign-in-method",
        to: "/sign-in-method",
        label: "nav.signInMethod",
        icon: Fingerprint,
        permission: "settings:read",
      },
      { id: "groups", to: "/groups", label: "nav.groups", icon: UsersRound, permission: "groups:read" },
      { id: "roles", to: "/roles", label: "nav.roles", icon: Shield, permission: "roles:read" },
      {
        id: "custom-fields",
        to: "/custom-fields",
        label: "nav.customFields",
        icon: ListChecks,
        permission: "settings:read",
      },
    ],
  },
  {
    id: "applications",
    label: "nav.applications",
    docs: "integrate/nextjs.md",
    items: [
      {
        id: "oauth-clients",
        to: "/oauth-clients",
        label: "nav.oauthClients",
        icon: KeyRound,
        permission: "clients:read",
      },
    ],
  },
  {
    id: "monitor",
    label: "nav.monitor",
    docs: "events.md",
    items: [
      {
        id: "audit-logs",
        to: "/audit-logs",
        label: "nav.auditLogs",
        icon: ScrollText,
        permission: "logs:read",
      },
    ],
  },
  {
    id: "settings",
    label: "nav.settings",
    docs: "runbook.md",
    items: [
      {
        id: "user-types",
        to: "/settings/user-types",
        label: "nav.userTypes",
        icon: IdCard,
        permission: "settings:read",
      },
      {
        id: "branding",
        to: "/settings/branding",
        label: "nav.branding",
        icon: Palette,
        permission: "settings:read",
      },
      { id: "data", to: "/settings/data", label: "nav.data", icon: Database, permission: "data:export" },
      {
        id: "log-retention",
        to: "/settings/log-retention",
        label: "nav.logRetention",
        icon: FileClock,
        permission: "settings:read",
      },
    ],
  },
];

export function activeItem(pathname: string): { section: NavSection | null; item: NavItem } {
  for (const s of SECTIONS) {
    for (const i of s.items)
      if (pathname === i.to || pathname.startsWith(`${i.to}/`)) return { section: s, item: i };
  }
  return { section: null, item: OVERVIEW };
}
